import React, { useState, useCallback, useEffect } from "react";
import { DataField, ToggleField } from "../shared/DataField";
import EditControls from "../shared/EditControls";
import { formatDateForDisplay } from "../../../utils/formatters";
import {
  EMPLOYMENT_STATUS_OPTIONS,
  shouldDisableEmploymentDetails,
  RELATIONSHIP_OPTIONS,
  SUFFIX_OPTIONS,
} from "../../../constants/membersConstants";
import StateCitySelect from "../../StateCitySelect";
import { COUNTRY_OPTIONS } from "../../../constants/projectConstants";
import { US_STATES } from "../../../data/usStatesAndCities";
import { resolveStateName } from "../../../utils/stateUtils";
import { isCustodialMinorOwner } from "../../../utils/memberLinks";
import {
  getBrokerageFlags,
  showIdInfo,
  showMaritalStatus,
} from "../../../utils/brokerageFields";

const TITLE_OPTIONS = ["Mr.", "Mrs.", "Ms.", "Dr.", "Prof."];
const MARITAL_OPTIONS = ["Single", "Married", "Divorced", "Widowed", "Separated", "Domestic Partner"];
const MEMBER_DETAIL_TABS = [
  { key: "personal", label: "Personal Detail" },
  { key: "contact", label: "Contact Details" },
  { key: "employment", label: "Employment Details" },
  { key: "id", label: "ID Details" },
];

const ReviewMembersTab = ({
  householdData,
  isEditing,
  formData,
  onChange,
  onEdit,
  onSave,
  onCancel,
  saving,
  validationErrors,
  onFieldError,
  onAddHouseholdMember,
}) => {
  const [groupTab, setGroupTab] = useState("owners");
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [detailTab, setDetailTab] = useState("personal");

  const members = householdData?.members || [];
  const brokerageFlags = getBrokerageFlags(householdData || {});
  const idInfoVisible = showIdInfo(brokerageFlags);
  const memberDetailTabs = idInfoVisible
    ? MEMBER_DETAIL_TABS
    : MEMBER_DETAIL_TABS.filter((tab) => tab.key !== "id");

  const isOwner = useCallback(
    (memberId) => {
      if (!householdData?.accounts || !Array.isArray(householdData.accounts)) return false;
      return householdData.accounts.some((account) => {
        const info = account.owners_info || account.owners;
        if (!info || !Array.isArray(info)) return false;
        return info.some((owner) => owner && (owner.id === memberId || owner.member_id === memberId));
      });
    },
    [householdData],
  );

  const owners = members.filter((m) => isOwner(m.id));
  const additionalMembers = members.filter((m) => !isOwner(m.id));

  const currentGroup = groupTab === "owners" ? owners : additionalMembers;

  useEffect(() => {
    if (currentGroup.length > 0 && (!selectedMemberId || !currentGroup.find((m) => m.id === selectedMemberId))) {
      setSelectedMemberId(currentGroup[0].id);
    }
  }, [currentGroup, selectedMemberId]);

  const selectedMember = members.find((m) => m.id === selectedMemberId);
  const personalInfo = selectedMember?.personal_info || {};
  const employmentInfo = selectedMember?.employment_info || {};

  const getAddress = () => {
    const addresses = personalInfo.address;
    if (!addresses) return null;
    const addrArray = Array.isArray(addresses) ? addresses : [addresses];
    return addrArray.find((a) => a.type === "home") || addrArray[0] || null;
  };

  const address = getAddress();

  const getFormValue = (path, fallback) => {
    if (!formData || !selectedMemberId) return fallback;
    const memberData = formData[selectedMemberId];
    if (!memberData) return fallback;
    const keys = path.split(".");
    let current = memberData;
    for (const key of keys) {
      if (current && current[key] !== undefined) {
        current = current[key];
      } else {
        return fallback;
      }
    }
    return current !== undefined ? current : fallback;
  };
  const selectedEmploymentStatus = getFormValue(
    "employment_info.status",
    employmentInfo.status || "",
  );
  const disableEmploymentDetails = shouldDisableEmploymentDetails(selectedEmploymentStatus);

  const handleFieldChange = (fieldPath, value) => {
    if (onChange && selectedMemberId) {
      onChange(selectedMemberId, fieldPath, value);
    }
  };

  const getMemberName = (m) => {
    const pi = m.personal_info || {};
    return `${pi.first_name || ""} ${pi.last_name || ""}`.trim() || "Unnamed";
  };

  const getMemberInitials = (m) => {
    const pi = m.personal_info || {};
    return `${(pi.first_name || "?").charAt(0)}${(pi.last_name || "?").charAt(0)}`.toUpperCase();
  };

  return (
    <div className="px-1 sm:px-0">
      {/* Header with Edit controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Household Members</h2>
        <div className="flex flex-wrap items-center gap-2">
          {onAddHouseholdMember && !isEditing && (
            <button
              type="button"
              onClick={onAddHouseholdMember}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add member
            </button>
          )}
          <EditControls
            isEditing={isEditing}
            onEdit={onEdit}
            onSave={onSave}
            onCancel={onCancel}
            saving={saving}
            disabled={Object.keys(validationErrors || {}).length > 0}
          />
        </div>
      </div>

      {/* Group sub-tabs: Owners / Additional Members */}
      <div className="flex gap-6 mb-4 flex-wrap sm:flex-nowrap overflow-x-auto pb-1">
        <button
          onClick={() => { setGroupTab("owners"); setSelectedMemberId(null); setDetailTab("personal"); }}
          className={`text-[0.9375rem] font-medium pb-1 border-b-2 transition-colors ${
            groupTab === "owners" ? "text-gray-900 border-gray-900" : "text-gray-500 border-transparent hover:text-gray-700"
          }`}
        >
          Account Owners ({owners.length})
        </button>
        <button
          onClick={() => { setGroupTab("additional"); setSelectedMemberId(null); setDetailTab("personal"); }}
          className={`text-[0.9375rem] font-medium pb-1 border-b-2 transition-colors ${
            groupTab === "additional" ? "text-gray-900 border-gray-900" : "text-gray-500 border-transparent hover:text-gray-700"
          }`}
        >
          Additional Members ({additionalMembers.length})
        </button>
      </div>

      {/* Member selector pills */}
      {currentGroup.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No {groupTab === "owners" ? "account owners" : "additional members"} found.</p>
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-4 flex-nowrap overflow-x-auto md:flex-wrap md:overflow-visible pb-1 -mx-1 px-1 md:mx-0 md:px-0">
            {currentGroup.map((m) => (
              <button
                key={m.id}
                onClick={() => { setSelectedMemberId(m.id); setDetailTab("personal"); }}
                className={`inline-flex shrink-0 items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedMemberId === m.id
                    ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  selectedMemberId === m.id ? "bg-emerald-500 text-white" : "bg-gray-300 text-gray-600"
                }`}>
                  {getMemberInitials(m)}
                </span>
                {getMemberName(m)}
              </button>
            ))}
          </div>

          {/* Detail tabs */}
          {selectedMember && (
            <>
              <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto pb-px -mx-1 px-1 md:mx-0 md:px-0">
                {memberDetailTabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setDetailTab(tab.key)}
                    className={`shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      detailTab === tab.key
                        ? "text-emerald-700 border-emerald-500"
                        : "text-gray-500 border-transparent hover:text-gray-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Personal Detail */}
              {detailTab === "personal" && (
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Personal Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                    <DataField
                      label="Title"
                      value={personalInfo.title || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      options={TITLE_OPTIONS}
                      fieldPath="personal_info.title"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    <DataField
                      label="First Name"
                      value={personalInfo.first_name || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="personal_info.first_name"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    <DataField
                      label="Middle Name"
                      value={personalInfo.middle_name || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="personal_info.middle_name"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    <DataField
                      label="Last Name"
                      value={personalInfo.last_name || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="personal_info.last_name"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    <DataField
                      label="Suffix"
                      value={personalInfo.suffix || ""}
                      isEditable
                      isEditing={isEditing}
                      options={SUFFIX_OPTIONS}
                      fieldPath="personal_info.suffix"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    {showMaritalStatus(brokerageFlags) && (
                    <DataField
                      label="Marital Status"
                      value={personalInfo.marital_status || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      options={MARITAL_OPTIONS}
                      fieldPath="personal_info.marital_status"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    )}
                    <DataField
                      label="SSN/TIN"
                      value={personalInfo.ssn_or_tin || ""}
                      isEditable
                      isEditing={isEditing}
                      type="ssn"
                      fieldPath="personal_info.ssn_or_tin"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                      error={validationErrors?.[`${selectedMemberId}_personal_info.ssn_or_tin`]}
                      onFieldError={(path, err) => onFieldError && onFieldError(`${selectedMemberId}_${path}`, err)}
                    />
                    <DataField
                      label="Date of Birth"
                      value={formatDateForDisplay(personalInfo.dob)}
                      isEditable
                      isEditing={isEditing}
                      type="date"
                      dateValue={personalInfo.dob}
                      fieldPath="personal_info.dob"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                      dateConstraint={
                        selectedMember?.is_primary &&
                        !isCustodialMinorOwner(householdData?.accounts, selectedMemberId)
                          ? "dob_primary"
                          : "dob_non_primary"
                      }
                      error={validationErrors?.[`${selectedMemberId}_personal_info.dob`]}
                      onFieldError={(path, err) =>
                        onFieldError && onFieldError(`${selectedMemberId}_${path}`, err)
                      }
                    />
                    <DataField
                      label="Email"
                      value={personalInfo.email || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      type="email"
                      fieldPath="personal_info.email"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    {isOwner(selectedMemberId) && (
                      <DataField
                        label="Username"
                        value={personalInfo.username || "N/A"}
                        isEditable
                        isEditing={isEditing}
                        fieldPath="personal_info.username"
                        onFieldChange={handleFieldChange}
                        getFormValue={getFormValue}
                      />
                    )}
                    <DataField
                      label="Country of Citizenship"
                      value={
                        personalInfo.country_of_citizenship != null &&
                        String(personalInfo.country_of_citizenship).trim() !== ""
                          ? personalInfo.country_of_citizenship
                          : "USA"
                      }
                      isEditable
                      isEditing={isEditing}
                      options={COUNTRY_OPTIONS.map((opt) => opt.value)}
                      fieldPath="personal_info.country_of_citizenship"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    {!selectedMember.is_primary && (
                      <DataField
                        label="Relationship to Primary Member"
                        value={personalInfo.relationship || "N/A"}
                        isEditable
                        isEditing={isEditing}
                        options={RELATIONSHIP_OPTIONS}
                        fieldPath="personal_info.relationship"
                        onFieldChange={handleFieldChange}
                        getFormValue={getFormValue}
                      />
                    )}
                    <ToggleField
                      label="Deceased"
                      value={personalInfo.is_deceased}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="personal_info.is_deceased"
                      onFieldChange={handleFieldChange}
                      formData={formData?.[selectedMemberId]}
                      variant="field"
                    />
                    {getFormValue("personal_info.is_deceased", personalInfo.is_deceased) && (
                      <DataField
                        label="Date of Death"
                        value={formatDateForDisplay(personalInfo.date_of_death)}
                        isEditable
                        isEditing={isEditing}
                        type="date"
                        dateValue={personalInfo.date_of_death}
                        fieldPath="personal_info.date_of_death"
                        onFieldChange={handleFieldChange}
                        getFormValue={getFormValue}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Contact Details */}
              {detailTab === "contact" && (
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Contact Details</h3>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Home Address</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                    <DataField
                      label="Address Line 1"
                      value={address?.address_line_1 || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="personal_info.address.home.address_line_1"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    <DataField
                      label="Address Line 2"
                      value={address?.address_line_2 || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="personal_info.address.home.address_line_2"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    {isEditing ? (
                      <div className="md:col-span-2 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                        <StateCitySelect
                          countryValue={getFormValue("personal_info.address.home.country", address?.country || "USA")}
                          stateValue={getFormValue("personal_info.address.home.province", address?.province || address?.state || "")}
                          cityValue={getFormValue("personal_info.address.home.city", address?.city || "")}
                          onCountryChange={(val) => handleFieldChange("personal_info.address.home.country", val)}
                          onStateChange={(val) => {
                            handleFieldChange("personal_info.address.home.province", val);
                            if (!getFormValue("personal_info.address.home.country", address?.country)) {
                              handleFieldChange("personal_info.address.home.country", "USA");
                            }
                          }}
                          onCityChange={(val) => {
                            handleFieldChange("personal_info.address.home.city", val);
                            if (!getFormValue("personal_info.address.home.country", address?.country)) {
                              handleFieldChange("personal_info.address.home.country", "USA");
                            }
                          }}
                          isEditing={true}
                          stateLabel="State"
                          cityLabel="City"
                        />
                      </div>
                    ) : (
                      <>
                        <DataField label="City" value={address?.city || "N/A"} />
                        <DataField label="State" value={resolveStateName(address?.province || address?.state) || "N/A"} />
                        <DataField label="Country" value={address?.country || "N/A"} />
                      </>
                    )}
                    <DataField
                      label="Zip Code"
                      value={address?.postal_code || address?.zip_code || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      fieldPath="personal_info.address.home.postal_code"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                      maxLength={5}
                    />
                  </div>
                  {(() => {
                    const addresses = personalInfo.address;
                    const addrArray = Array.isArray(addresses) ? addresses : [addresses].filter(Boolean);
                    const mailingAddress = addrArray.find((a) => a.type === "mailing");
                    if (!mailingAddress) return null;
                    return (
                      <div className="mt-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Mailing Address</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                          <DataField label="Address Line 1" value={mailingAddress?.address_line_1 || "N/A"} isEditable isEditing={isEditing} fieldPath="personal_info.address.mailing.address_line_1" onFieldChange={handleFieldChange} getFormValue={getFormValue} />
                          <DataField label="Address Line 2" value={mailingAddress?.address_line_2 || "N/A"} isEditable isEditing={isEditing} fieldPath="personal_info.address.mailing.address_line_2" onFieldChange={handleFieldChange} getFormValue={getFormValue} />
                          {isEditing ? (
                            <div className="md:col-span-2 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                              <StateCitySelect
                                countryValue={getFormValue("personal_info.address.mailing.country", mailingAddress?.country || "USA")}
                                stateValue={getFormValue("personal_info.address.mailing.province", mailingAddress?.province || "")}
                                cityValue={getFormValue("personal_info.address.mailing.city", mailingAddress?.city || "")}
                                onCountryChange={(val) => handleFieldChange("personal_info.address.mailing.country", val)}
                                onStateChange={(val) => {
                                  handleFieldChange("personal_info.address.mailing.province", val);
                                  if (!getFormValue("personal_info.address.mailing.country", mailingAddress?.country)) {
                                    handleFieldChange("personal_info.address.mailing.country", "USA");
                                  }
                                }}
                                onCityChange={(val) => {
                                  handleFieldChange("personal_info.address.mailing.city", val);
                                  if (!getFormValue("personal_info.address.mailing.country", mailingAddress?.country)) {
                                    handleFieldChange("personal_info.address.mailing.country", "USA");
                                  }
                                }}
                                isEditing={true}
                                stateLabel="State"
                                cityLabel="City"
                              />
                            </div>
                          ) : (
                            <>
                              <DataField label="City" value={mailingAddress?.city || "N/A"} />
                              <DataField label="State" value={resolveStateName(mailingAddress?.province) || "N/A"} />
                              <DataField label="Country" value={mailingAddress?.country || "N/A"} />
                            </>
                          )}
                          <DataField label="ZIP Code" value={mailingAddress?.postal_code || "N/A"} isEditable isEditing={isEditing} fieldPath="personal_info.address.mailing.postal_code" onFieldChange={handleFieldChange} getFormValue={getFormValue} maxLength={5} />
                          <DataField label="Phone" value={mailingAddress?.phone || "N/A"} isEditable isEditing={isEditing} type="phone" fieldPath="personal_info.address.mailing.phone" onFieldChange={handleFieldChange} getFormValue={getFormValue} />
                        </div>
                      </div>
                    );
                  })()}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                    <DataField
                      label="Phone"
                      value={address?.phone || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      type="phone"
                      fieldPath="personal_info.address.home.phone"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                  </div>
                </div>
              )}

              {/* Employment Details */}
              {detailTab === "employment" && (
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Employment Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                    <DataField
                      label="Employment Status"
                      value={employmentInfo.status || "N/A"}
                      isEditable
                      isEditing={isEditing}
                      options={EMPLOYMENT_STATUS_OPTIONS}
                      fieldPath="employment_info.status"
                      onFieldChange={handleFieldChange}
                      getFormValue={getFormValue}
                    />
                    {!disableEmploymentDetails && (
                      <>
                        <DataField
                          label="Occupation"
                          value={employmentInfo.occupation || "N/A"}
                          isEditable
                          isEditing={isEditing}
                          fieldPath="employment_info.occupation"
                          onFieldChange={handleFieldChange}
                          getFormValue={getFormValue}
                        />
                        {/* industry removed from UI */}
                        <DataField
                          label="Employer"
                          value={employmentInfo.employer || "N/A"}
                          isEditable
                          isEditing={isEditing}
                          fieldPath="employment_info.employer"
                          onFieldChange={handleFieldChange}
                          getFormValue={getFormValue}
                        />
                        <DataField
                          label="Job Title"
                          value={employmentInfo.title || "N/A"}
                          isEditable
                          isEditing={isEditing}
                          fieldPath="employment_info.title"
                          onFieldChange={handleFieldChange}
                          getFormValue={getFormValue}
                        />
                        <DataField
                          label="Employer TIN/EIN"
                          value={employmentInfo.tin || "N/A"}
                          isEditable
                          isEditing={isEditing}
                          type="ssn"
                          ssnLengthOnly
                          fieldPath="employment_info.tin"
                          onFieldChange={handleFieldChange}
                          getFormValue={getFormValue}
                          error={validationErrors?.[`${selectedMemberId}_employment_info.tin`]}
                          onFieldError={(path, err) => onFieldError && onFieldError(`${selectedMemberId}_${path}`, err)}
                        />
                      </>
                    )}
                  </div>
                  {!disableEmploymentDetails && (
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Work Address</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                        <DataField
                          label="Address Line 1"
                          value={employmentInfo.address?.address_line_1 || "N/A"}
                          isEditable
                          isEditing={isEditing}
                          fieldPath="employment_info.address.address_line_1"
                          onFieldChange={handleFieldChange}
                          getFormValue={getFormValue}
                        />
                        <DataField
                          label="Address Line 2"
                          value={employmentInfo.address?.address_line_2 || "N/A"}
                          isEditable
                          isEditing={isEditing}
                          fieldPath="employment_info.address.address_line_2"
                          onFieldChange={handleFieldChange}
                          getFormValue={getFormValue}
                        />
                        {isEditing ? (
                          <div className="md:col-span-2 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                            <StateCitySelect
                              countryValue={getFormValue("employment_info.address.country", employmentInfo.address?.country || "USA")}
                              stateValue={getFormValue("employment_info.address.province", employmentInfo.address?.province || "")}
                              cityValue={getFormValue("employment_info.address.city", employmentInfo.address?.city || "")}
                              onCountryChange={(val) => handleFieldChange("employment_info.address.country", val)}
                              onStateChange={(val) => {
                                handleFieldChange("employment_info.address.province", val);
                                if (!getFormValue("employment_info.address.country", employmentInfo.address?.country)) {
                                  handleFieldChange("employment_info.address.country", "USA");
                                }
                              }}
                              onCityChange={(val) => {
                                handleFieldChange("employment_info.address.city", val);
                                if (!getFormValue("employment_info.address.country", employmentInfo.address?.country)) {
                                  handleFieldChange("employment_info.address.country", "USA");
                                }
                              }}
                              isEditing={true}
                              stateLabel="State"
                              cityLabel="City"
                            />
                          </div>
                        ) : (
                          <>
                            <DataField label="City" value={employmentInfo.address?.city || "N/A"} />
                            <DataField label="State" value={resolveStateName(employmentInfo.address?.province) || "N/A"} />
                            <DataField label="Country" value={employmentInfo.address?.country || "N/A"} />
                          </>
                        )}
                        <DataField
                          label="ZIP Code"
                          value={employmentInfo.address?.postal_code || "N/A"}
                          isEditable
                          isEditing={isEditing}
                          fieldPath="employment_info.address.postal_code"
                          onFieldChange={handleFieldChange}
                          getFormValue={getFormValue}
                          maxLength={5}
                        />
                        <DataField
                          label="Phone"
                          value={employmentInfo.address?.phone || "N/A"}
                          isEditable
                          isEditing={isEditing}
                          type="phone"
                          fieldPath="employment_info.address.phone"
                          onFieldChange={handleFieldChange}
                          getFormValue={getFormValue}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ID Details */}
              {idInfoVisible && detailTab === "id" && (
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">ID Details</h3>
                  {(() => {
                    const idInfoArray = Array.isArray(selectedMember?.id_info) ? selectedMember.id_info : [];
                    if (idInfoArray.length === 0) {
                      return (
                        <div className="border border-gray-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">ID #1</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                            <DataField label="ID Type" value="N/A" isEditable isEditing={isEditing} fieldPath="id_info.0.type" onFieldChange={handleFieldChange} getFormValue={getFormValue} />
                            <DataField label="Issue State" value="N/A" isEditable isEditing={isEditing} fieldPath="id_info.0.issue_state" onFieldChange={handleFieldChange} getFormValue={getFormValue} options={US_STATES.map(s => s.abbreviation)} optionLabels={US_STATES.map(s => `${s.name} (${s.abbreviation})`)} />
                            <DataField label="ID Number" value="N/A" isEditable isEditing={isEditing} fieldPath="id_info.0.number" onFieldChange={handleFieldChange} getFormValue={getFormValue} />
                            <DataField label="Issuance Date" value="N/A" isEditable isEditing={isEditing} type="date" fieldPath="id_info.0.issuance_date" onFieldChange={handleFieldChange} getFormValue={getFormValue} />
                            <DataField label="Expiration Date" value="N/A" isEditable isEditing={isEditing} type="date" fieldPath="id_info.0.expiration_date" onFieldChange={handleFieldChange} getFormValue={getFormValue} />
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-4">
                        {idInfoArray.map((idItem, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">ID #{index + 1}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                              <DataField
                                label="ID Type"
                                value={idItem.type || "N/A"}
                                isEditable
                                isEditing={isEditing}
                                fieldPath={`id_info.${index}.type`}
                                onFieldChange={handleFieldChange}
                                getFormValue={getFormValue}
                              />
                              <DataField
                                label="Issue State"
                                value={resolveStateName(idItem.issue_state) || "N/A"}
                                isEditable
                                isEditing={isEditing}
                                fieldPath={`id_info.${index}.issue_state`}
                                onFieldChange={handleFieldChange}
                                getFormValue={getFormValue}
                                options={US_STATES.map(s => s.abbreviation)}
                                optionLabels={US_STATES.map(s => `${s.name} (${s.abbreviation})`)}
                              />
                              <DataField
                                label="ID Number"
                                value={idItem.number || "N/A"}
                                isEditable
                                isEditing={isEditing}
                                fieldPath={`id_info.${index}.number`}
                                onFieldChange={handleFieldChange}
                                getFormValue={getFormValue}
                              />
                              <DataField
                                label="Issuance Date"
                                value={formatDateForDisplay(idItem.issuance_date)}
                                isEditable
                                isEditing={isEditing}
                                type="date"
                                dateValue={idItem.issuance_date}
                                fieldPath={`id_info.${index}.issuance_date`}
                                onFieldChange={handleFieldChange}
                                getFormValue={getFormValue}
                              />
                              <DataField
                                label="Expiration Date"
                                value={formatDateForDisplay(idItem.expiration_date)}
                                isEditable
                                isEditing={isEditing}
                                type="date"
                                dateValue={idItem.expiration_date}
                                fieldPath={`id_info.${index}.expiration_date`}
                                onFieldChange={handleFieldChange}
                                getFormValue={getFormValue}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewMembersTab;
